export type SurfaceSnapshot<TItem> = {
  savedAt: string
  expiresAt: string
  items: TItem[]
}

export type SurfaceExpiredSnapshot = {
  savedAt?: string
  expiresAt?: string
  itemsCount: number
}

export type SurfaceSnapshotReadResult<TItem> = {
  snapshot: SurfaceSnapshot<TItem> | null
  expiredSnapshot: SurfaceExpiredSnapshot | null
}

type SurfaceSnapshotStoreOptions<TSourceItem, TSnapshotItem> = {
  key: string
  ttlMs: number
  limit?: number
  storage?: Storage
  sanitizeItem: (item: TSourceItem) => TSnapshotItem | null | undefined
  validateItem?: (item: unknown) => item is TSnapshotItem
}

const FORBIDDEN_SNAPSHOT_KEYS = new Set([
  'prompt',
  'referenceImage',
  'referenceImages',
  'referencePayload',
  'referencePayloads',
  'token',
  'provider',
  'providerMetadata',
  'model',
  'modelId',
  'requestedModelId',
  'resolvedModelId',
  'requestedProvider',
  'resolvedProvider'
])

const getDefaultStorage = () => (
  typeof window !== 'undefined' ? window.sessionStorage : undefined
)

const stripForbiddenSnapshotFields = <TItem>(item: TItem): TItem => {
  if (!item || typeof item !== 'object' || Array.isArray(item)) {
    return item
  }

  return Object.fromEntries(
    Object.entries(item as Record<string, unknown>)
      .filter(([key]) => !FORBIDDEN_SNAPSHOT_KEYS.has(key))
  ) as TItem
}

export const createSurfaceSnapshotStore = <TSourceItem, TSnapshotItem>({
  key,
  ttlMs,
  limit = Number.POSITIVE_INFINITY,
  storage = getDefaultStorage(),
  sanitizeItem,
  validateItem
}: SurfaceSnapshotStoreOptions<TSourceItem, TSnapshotItem>) => {
  const clear = () => {
    try {
      storage?.removeItem(key)
    } catch {
      // Snapshot storage is best-effort.
    }
  }

  const read = (now = Date.now()): SurfaceSnapshotReadResult<TSnapshotItem> => {
    try {
      const raw = storage?.getItem(key)
      if (!raw) {
        return { snapshot: null, expiredSnapshot: null }
      }

      const parsed = JSON.parse(raw)
      const expiresAtMs = typeof parsed?.expiresAt === 'string'
        ? new Date(parsed.expiresAt).getTime()
        : NaN

      if (!Number.isFinite(expiresAtMs) || expiresAtMs <= now) {
        clear()
        return {
          snapshot: null,
          expiredSnapshot: {
            savedAt: typeof parsed?.savedAt === 'string' ? parsed.savedAt : undefined,
            expiresAt: typeof parsed?.expiresAt === 'string' ? parsed.expiresAt : undefined,
            itemsCount: Array.isArray(parsed?.items) ? parsed.items.length : 0
          }
        }
      }

      const items = Array.isArray(parsed.items)
        ? parsed.items
            .filter((item: unknown) => validateItem ? validateItem(item) : true)
            .map((item: unknown) => stripForbiddenSnapshotFields(item as TSnapshotItem))
            .slice(0, limit)
        : []

      if (items.length === 0) {
        return { snapshot: null, expiredSnapshot: null }
      }

      return {
        snapshot: {
          savedAt: typeof parsed.savedAt === 'string' ? parsed.savedAt : new Date(now).toISOString(),
          expiresAt: parsed.expiresAt,
          items
        },
        expiredSnapshot: null
      }
    } catch {
      clear()
      return { snapshot: null, expiredSnapshot: null }
    }
  }

  const write = (items: TSourceItem[], now = Date.now()) => {
    try {
      const snapshotItems = items
        .slice(0, limit)
        .map(sanitizeItem)
        .filter((item): item is TSnapshotItem => Boolean(item))
        .map(stripForbiddenSnapshotFields)

      if (snapshotItems.length === 0) {
        clear()
        return
      }

      storage?.setItem(key, JSON.stringify({
        savedAt: new Date(now).toISOString(),
        expiresAt: new Date(now + ttlMs).toISOString(),
        items: snapshotItems
      }))
    } catch {
      // Snapshot storage is best-effort.
    }
  }

  return {
    clear,
    read,
    write
  }
}
